import { useState, type DragEventHandler, type KeyboardEventHandler, type ReactNode } from "react";

import type { TaxonomyNodeRecord, TileRecord, TileSetRecord } from "@west-marches/shared";
import { TILE_SET_DYNAMIC_BADGE, TILE_SET_ORDERED_BADGE } from "@/lib/tileSetUiLabels";

import {
  getTileRecordPreviewUrl,
  type SystemCatalogSelection,
  type TaxonomyMixedEntry,
} from "./systemCatalogAdminModel";
import type { SectionCardProps } from "./systemCatalogAdminTypes";
import {
  decodeSystemCatalogDragPayload,
  encodeSystemCatalogDragPayload,
  SYSTEM_CATALOG_DRAG_MIME,
  type SystemCatalogDragPayload,
} from "./systemCatalogAdminTypes";
import { classNames } from "./systemCatalogAdminUtils";
import { getEffectiveTileSetPaintMode } from "./tileSetPaintMode";

export function ActionButton({
  children,
  onClick,
  disabled = false,
  tone = "default",
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "destructive";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        "rounded-lg border-2 px-2.5 py-1.5 text-xs font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d] disabled:cursor-not-allowed disabled:opacity-40",
        tone === "destructive"
          ? "border-2 border-neutral-950 bg-[#9c4c32] text-[#fff7ec] hover:bg-[#5d2f26] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.08)]"
          : "border-2 border-neutral-950 bg-transparent text-neutral-950 hover:bg-[#fff2d9] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_0_rgba(0,0,0,0.06)]",
      )}
    >
      {children}
    </button>
  );
}

export function SectionCard({ title, description, children }: SectionCardProps) {
  return (
    <section className="rounded-2xl border-[3px] border-neutral-950 bg-[#fffaf1] p-5 shadow-[8px_8px_0_rgba(19,19,19,0.1)]">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-950">{title}</h2>
        {description ? <p className="mt-1 text-sm text-neutral-600">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function EmptyGridState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-neutral-950/20 bg-neutral-950/[0.035] px-4 py-6 text-sm text-neutral-600">
      {message}
    </div>
  );
}

export function ReorderDropSlot({
  active = false,
  enabled = true,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
}: {
  active?: boolean;
  enabled?: boolean;
  onDragOver: DragEventHandler<HTMLDivElement>;
  onDragEnter?: DragEventHandler<HTMLDivElement>;
  onDragLeave?: DragEventHandler<HTMLDivElement>;
  onDrop: DragEventHandler<HTMLDivElement>;
}) {
  return (
    <div
      aria-hidden={!enabled}
      onDragOver={enabled ? onDragOver : undefined}
      onDragEnter={enabled ? onDragEnter : undefined}
      onDragLeave={enabled ? onDragLeave : undefined}
      onDrop={enabled ? onDrop : undefined}
      className={classNames(
        "rounded-xl border border-dashed transition",
        enabled ? "h-4 opacity-100" : "h-0 border-transparent opacity-0",
        active ? "border-[#3e664d]/50 bg-[#3e664d]/10" : "border-neutral-950/15 bg-transparent",
      )}
    />
  );
}

export function TaxonomyStateBadge({ isDraft }: { isDraft: boolean }) {
  return (
    <span
      className={classNames(
        "rounded-full border-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]",
        isDraft
          ? "border-neutral-950 bg-[#fff2d9] text-neutral-950"
          : "border-[#3e664d] bg-[#3e664d]/10 text-[#3e664d]",
      )}
    >
      {isDraft ? "Draft" : "Live"}
    </span>
  );
}

export function TaxonomyNavTree({
  nodes,
  selectedId,
  allNodes,
  onSelect,
  getDragPayload,
  onDropPayload,
  depth = 0,
}: {
  nodes: TaxonomyNodeRecord[];
  selectedId?: string;
  allNodes: TaxonomyNodeRecord[];
  onSelect: (id: string) => void;
  getDragPayload?: (node: TaxonomyNodeRecord) => SystemCatalogDragPayload | null;
  onDropPayload?: (targetId: string, payload: SystemCatalogDragPayload) => void;
  depth?: number;
}) {
  const [activeDropId, setActiveDropId] = useState<string | null>(null);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {nodes.map((node) => {
        const children = allNodes.filter((entry) => entry.parent_id === node.id);

        return (
          <div key={node.id}>
            <button
              type="button"
              onClick={() => onSelect(node.id)}
              draggable={Boolean(getDragPayload?.(node))}
              onDragStart={(event) => {
                const payload = getDragPayload?.(node);
                if (!payload) {
                  return;
                }
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData(
                  SYSTEM_CATALOG_DRAG_MIME,
                  encodeSystemCatalogDragPayload(payload),
                );
              }}
              onDragOver={(event) => {
                if (!onDropPayload || !event.dataTransfer.types.includes(SYSTEM_CATALOG_DRAG_MIME)) {
                  return;
                }
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
              }}
              onDragEnter={(event) => {
                if (!onDropPayload || !event.dataTransfer.types.includes(SYSTEM_CATALOG_DRAG_MIME)) {
                  return;
                }
                event.preventDefault();
                setActiveDropId(node.id);
              }}
              onDragLeave={() => {
                if (activeDropId === node.id) {
                  setActiveDropId(null);
                }
              }}
              onDrop={(event) => {
                if (!onDropPayload) {
                  return;
                }
                event.preventDefault();
                setActiveDropId(null);
                const payload = decodeSystemCatalogDragPayload(
                  event.dataTransfer.getData(SYSTEM_CATALOG_DRAG_MIME),
                );
                if (payload) {
                  onDropPayload(node.id, payload);
                }
              }}
              className={classNames(
                "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition",
                selectedId === node.id
                  ? "bg-[#fff2d9] text-neutral-950"
                  : "text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
                getDragPayload?.(node) ? "cursor-grab active:cursor-grabbing" : "",
                activeDropId === node.id ? "ring-2 ring-[#3e664d]/40" : "",
              )}
              style={{ paddingLeft: `${12 + depth * 14}px` }}
            >
              <span className="flex items-center gap-2">
                <span>{node.label}</span>
                <TaxonomyStateBadge isDraft={node.is_draft} />
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">node</span>
            </button>
            <TaxonomyNavTree
              nodes={children}
              selectedId={selectedId}
              allNodes={allNodes}
              onSelect={onSelect}
              getDragPayload={getDragPayload}
              onDropPayload={onDropPayload}
              depth={depth + 1}
            />
          </div>
        );
      })}
    </div>
  );
}

export function CatalogNavTree({
  selection,
  taxonomyMixedEntriesByParentId,
  tilesByTileSetId,
  taxonomyPreviewUrlById,
  tileSetPreviewUrlById,
  expandedKeys,
  onToggleExpanded,
  onSelectTaxonomy,
  onSelectTileSet,
  getTaxonomyDragPayload,
  getTileSetDragPayload,
  getTileDragPayload,
  onDropOnTaxonomy,
  onDropOnTileSet,
  disableDrag = false,
  selectedTileId = null,
  onSelectTile,
  depth = 0,
  parentId = null,
}: {
  selection: SystemCatalogSelection;
  taxonomyMixedEntriesByParentId: Map<string | null, TaxonomyMixedEntry[]>;
  tilesByTileSetId: Map<string, TileRecord[]>;
  taxonomyPreviewUrlById: Map<string, string | null>;
  tileSetPreviewUrlById: Map<string, string | null>;
  expandedKeys: Set<string>;
  onToggleExpanded: (key: string) => void;
  onSelectTaxonomy: (id: string) => void;
  onSelectTileSet: (id: string) => void;
  getTaxonomyDragPayload: (node: TaxonomyNodeRecord) => SystemCatalogDragPayload;
  getTileSetDragPayload: (tileSetId: string, sourceTaxonomyId: string | null) => SystemCatalogDragPayload;
  getTileDragPayload: (tile: TileRecord, sourceTileSetId: string) => SystemCatalogDragPayload;
  onDropOnTaxonomy: (targetId: string, payload: SystemCatalogDragPayload) => void;
  onDropOnTileSet: (targetTileSetId: string, payload: SystemCatalogDragPayload) => void;
  disableDrag?: boolean;
  selectedTileId?: string | null;
  onSelectTile?: (tile: TileRecord, tileSetId: string) => void;
  depth?: number;
  parentId?: string | null;
}) {
  const entries = taxonomyMixedEntriesByParentId.get(parentId) ?? [];
  const [activeDropKey, setActiveDropKey] = useState<string | null>(null);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {entries.map((entry) =>
        entry.kind === "taxonomy-node" ? (
          <CatalogTaxonomyTreeNode
            key={`taxonomy:${entry.node.id}`}
            node={entry.node}
            selection={selection}
            taxonomyMixedEntriesByParentId={taxonomyMixedEntriesByParentId}
            tilesByTileSetId={tilesByTileSetId}
            taxonomyPreviewUrlById={taxonomyPreviewUrlById}
            tileSetPreviewUrlById={tileSetPreviewUrlById}
            expandedKeys={expandedKeys}
            activeDropKey={activeDropKey}
            setActiveDropKey={setActiveDropKey}
            onToggleExpanded={onToggleExpanded}
            onSelectTaxonomy={onSelectTaxonomy}
            onSelectTileSet={onSelectTileSet}
            getTaxonomyDragPayload={getTaxonomyDragPayload}
            getTileSetDragPayload={getTileSetDragPayload}
            getTileDragPayload={getTileDragPayload}
            onDropOnTaxonomy={onDropOnTaxonomy}
            onDropOnTileSet={onDropOnTileSet}
            disableDrag={disableDrag}
            selectedTileId={selectedTileId}
            onSelectTile={onSelectTile}
            depth={depth}
          />
        ) : (
          <CatalogTileSetTreeNode
            key={`tile-set:${entry.tileSet.id}`}
            tileSet={entry.tileSet}
            tiles={tilesByTileSetId.get(entry.tileSet.id) ?? []}
            thumbnailUrl={tileSetPreviewUrlById.get(entry.tileSet.id) ?? null}
            selection={selection}
            expandedKeys={expandedKeys}
            activeDropKey={activeDropKey}
            setActiveDropKey={setActiveDropKey}
            onToggleExpanded={onToggleExpanded}
            onSelectTileSet={onSelectTileSet}
            getTileSetDragPayload={() => getTileSetDragPayload(entry.tileSet.id, parentId)}
            getTileDragPayload={getTileDragPayload}
            onDropOnTileSet={onDropOnTileSet}
            disableDrag={disableDrag}
            selectedTileId={selectedTileId}
            onSelectTile={onSelectTile}
            depth={depth}
          />
        ),
      )}
    </div>
  );
}

function CatalogTaxonomyTreeNode({
  node,
  selection,
  taxonomyMixedEntriesByParentId,
  tilesByTileSetId,
  taxonomyPreviewUrlById,
  tileSetPreviewUrlById,
  expandedKeys,
  activeDropKey,
  setActiveDropKey,
  onToggleExpanded,
  onSelectTaxonomy,
  onSelectTileSet,
  getTaxonomyDragPayload,
  getTileSetDragPayload,
  getTileDragPayload,
  onDropOnTaxonomy,
  onDropOnTileSet,
  disableDrag,
  selectedTileId,
  onSelectTile,
  depth,
}: {
  node: TaxonomyNodeRecord;
  selection: SystemCatalogSelection;
  taxonomyMixedEntriesByParentId: Map<string | null, TaxonomyMixedEntry[]>;
  tilesByTileSetId: Map<string, TileRecord[]>;
  taxonomyPreviewUrlById: Map<string, string | null>;
  tileSetPreviewUrlById: Map<string, string | null>;
  expandedKeys: Set<string>;
  activeDropKey: string | null;
  setActiveDropKey: (value: string | null) => void;
  onToggleExpanded: (key: string) => void;
  onSelectTaxonomy: (id: string) => void;
  onSelectTileSet: (id: string) => void;
  getTaxonomyDragPayload: (node: TaxonomyNodeRecord) => SystemCatalogDragPayload;
  getTileSetDragPayload: (tileSetId: string, sourceTaxonomyId: string | null) => SystemCatalogDragPayload;
  getTileDragPayload: (tile: TileRecord, sourceTileSetId: string) => SystemCatalogDragPayload;
  onDropOnTaxonomy: (targetId: string, payload: SystemCatalogDragPayload) => void;
  onDropOnTileSet: (targetTileSetId: string, payload: SystemCatalogDragPayload) => void;
  disableDrag: boolean;
  selectedTileId: string | null;
  onSelectTile?: (tile: TileRecord, tileSetId: string) => void;
  depth: number;
}) {
  const key = `taxonomy:${node.id}`;
  const isExpanded = expandedKeys.has(key);
  const childEntries = taxonomyMixedEntriesByParentId.get(node.id) ?? [];
  const hasChildren = childEntries.length > 0;

  return (
    <div>
      <TreeRow
        label={node.label}
        badge="folder"
        depth={depth}
        thumbnailUrl={taxonomyPreviewUrlById.get(node.id) ?? null}
        isSelected={selection.kind === "taxonomy" && selection.id === node.id}
        isExpanded={hasChildren ? isExpanded : undefined}
        onToggle={hasChildren ? () => onToggleExpanded(key) : undefined}
        onClick={() => onSelectTaxonomy(node.id)}
        dragPayload={disableDrag ? undefined : getTaxonomyDragPayload(node)}
        activeDrop={!disableDrag && activeDropKey === key}
        onDropPayload={
          disableDrag ? undefined : (payload) => onDropOnTaxonomy(node.id, payload)
        }
        onSetActiveDrop={
          disableDrag ? undefined : (value) => setActiveDropKey(value ? key : null)
        }
        detail={node.is_draft ? "draft" : undefined}
      />
      {isExpanded ? (
        <CatalogNavTree
          selection={selection}
          taxonomyMixedEntriesByParentId={taxonomyMixedEntriesByParentId}
          tilesByTileSetId={tilesByTileSetId}
          taxonomyPreviewUrlById={taxonomyPreviewUrlById}
          tileSetPreviewUrlById={tileSetPreviewUrlById}
          expandedKeys={expandedKeys}
          onToggleExpanded={onToggleExpanded}
          onSelectTaxonomy={onSelectTaxonomy}
          onSelectTileSet={onSelectTileSet}
          getTaxonomyDragPayload={getTaxonomyDragPayload}
          getTileSetDragPayload={getTileSetDragPayload}
          getTileDragPayload={getTileDragPayload}
          onDropOnTaxonomy={onDropOnTaxonomy}
          onDropOnTileSet={onDropOnTileSet}
          disableDrag={disableDrag}
          selectedTileId={selectedTileId}
          onSelectTile={onSelectTile}
          depth={depth + 1}
          parentId={node.id}
        />
      ) : null}
    </div>
  );
}

function CatalogTileSetTreeNode({
  tileSet,
  tiles,
  thumbnailUrl,
  selection,
  expandedKeys,
  activeDropKey,
  setActiveDropKey,
  onToggleExpanded,
  onSelectTileSet,
  getTileSetDragPayload,
  getTileDragPayload,
  onDropOnTileSet,
  disableDrag,
  selectedTileId,
  onSelectTile,
  depth,
}: {
  tileSet: TileSetRecord;
  tiles: TileRecord[];
  thumbnailUrl: string | null;
  selection: SystemCatalogSelection;
  expandedKeys: Set<string>;
  activeDropKey: string | null;
  setActiveDropKey: (value: string | null) => void;
  onToggleExpanded: (key: string) => void;
  onSelectTileSet: (id: string) => void;
  getTileSetDragPayload: () => SystemCatalogDragPayload;
  getTileDragPayload: (tile: TileRecord, sourceTileSetId: string) => SystemCatalogDragPayload;
  onDropOnTileSet: (targetTileSetId: string, payload: SystemCatalogDragPayload) => void;
  disableDrag: boolean;
  selectedTileId: string | null;
  onSelectTile?: (tile: TileRecord, tileSetId: string) => void;
  depth: number;
}) {
  const key = `tile-set:${tileSet.id}`;
  const isExpanded = expandedKeys.has(key);

  return (
    <div>
      <TreeRow
        label={tileSet.title}
        badge={
          getEffectiveTileSetPaintMode(tileSet, tiles.length) === "dynamic"
            ? TILE_SET_DYNAMIC_BADGE
            : TILE_SET_ORDERED_BADGE
        }
        depth={depth}
        thumbnailUrl={thumbnailUrl}
        isSelected={selection.kind === "tile-set" && selection.id === tileSet.id}
        isExpanded={tiles.length > 0 ? isExpanded : undefined}
        onToggle={tiles.length > 0 ? () => onToggleExpanded(key) : undefined}
        onClick={() => onSelectTileSet(tileSet.id)}
        dragPayload={disableDrag ? undefined : getTileSetDragPayload()}
        activeDrop={!disableDrag && activeDropKey === key}
        onDropPayload={
          disableDrag ? undefined : (payload) => onDropOnTileSet(tileSet.id, payload)
        }
        onSetActiveDrop={
          disableDrag ? undefined : (value) => setActiveDropKey(value ? key : null)
        }
        detail={`${tiles.length} tile${tiles.length === 1 ? "" : "s"}`}
      />
      {isExpanded ? (
        <div className="space-y-1">
          {tiles.map((tile) => (
            <TreeRow
              key={`tile:${tile.id}`}
              label={tile.filename}
              badge="tile"
              depth={depth + 1}
              thumbnailUrl={getTileRecordPreviewUrl(tile)}
              isSelected={selectedTileId === tile.id}
              onClick={() => {
                if (onSelectTile) {
                  onSelectTile(tile, tileSet.id);
                } else {
                  onSelectTileSet(tileSet.id);
                }
              }}
              dragPayload={disableDrag ? undefined : getTileDragPayload(tile, tileSet.id)}
              detail={tile.id}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TreeRow({
  label,
  badge,
  depth,
  isSelected = false,
  isExpanded,
  onToggle,
  onClick,
  dragPayload,
  activeDrop = false,
  onDropPayload,
  onSetActiveDrop,
  detail,
  thumbnailUrl,
}: {
  label: string;
  badge: string;
  depth: number;
  isSelected?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  onClick: () => void;
  dragPayload?: SystemCatalogDragPayload;
  activeDrop?: boolean;
  onDropPayload?: (payload: SystemCatalogDragPayload) => void;
  onSetActiveDrop?: (value: boolean) => void;
  detail?: string;
  thumbnailUrl?: string | null;
}) {
  const [hasLoadedHoverPreview, setHasLoadedHoverPreview] = useState(false);
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    onClick();
  };

  return (
    <div
      className={classNames(
        "flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[15px] transition",
        isSelected ? "bg-[#fff2d9] text-neutral-950" : "text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
        activeDrop ? "ring-2 ring-[#3e664d]/40" : "",
      )}
      style={{ paddingLeft: `${10 + depth * 16}px` }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => {
        if (thumbnailUrl) {
          setHasLoadedHoverPreview(true);
        }
      }}
      onFocus={() => {
        if (thumbnailUrl) {
          setHasLoadedHoverPreview(true);
        }
      }}
      draggable={Boolean(dragPayload)}
      onDragStart={(event) => {
        if (!dragPayload) {
          return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(SYSTEM_CATALOG_DRAG_MIME, encodeSystemCatalogDragPayload(dragPayload));
      }}
      onDragOver={(event) => {
        if (!onDropPayload || !event.dataTransfer.types.includes(SYSTEM_CATALOG_DRAG_MIME)) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDragEnter={(event) => {
        if (!onDropPayload || !event.dataTransfer.types.includes(SYSTEM_CATALOG_DRAG_MIME)) {
          return;
        }
        event.preventDefault();
        onSetActiveDrop?.(true);
      }}
      onDragLeave={() => onSetActiveDrop?.(false)}
      onDrop={(event) => {
        if (!onDropPayload) {
          return;
        }
        event.preventDefault();
        onSetActiveDrop?.(false);
        const payload = decodeSystemCatalogDragPayload(
          event.dataTransfer.getData(SYSTEM_CATALOG_DRAG_MIME),
        );
        if (payload) {
          onDropPayload(payload);
        }
      }}
    >
      {onToggle ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          className="w-6 shrink-0 cursor-pointer text-center text-sm text-neutral-600 hover:text-neutral-950"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? "▾" : "▸"}
        </button>
      ) : (
        <span className="w-6 shrink-0" />
      )}
      <span className="group relative h-7 w-7 shrink-0 overflow-visible">
        <span className="absolute inset-0 overflow-hidden rounded-md border border-neutral-950/20 bg-neutral-950/[0.035]">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              aria-hidden
            />
          ) : null}
        </span>
        {thumbnailUrl && hasLoadedHoverPreview ? (
          <span className="pointer-events-none absolute top-1/2 left-full z-30 ml-3 hidden w-64 -translate-y-1/2 rounded-xl border-[3px] border-neutral-950 bg-[#fffaf1] p-3 shadow-[10px_10px_0_rgba(19,19,19,0.14)] group-hover:block">
            <span className="block overflow-hidden rounded-lg border border-neutral-950/15 bg-[#f7f3eb]/60">
              <span className="relative block aspect-square w-full">
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  aria-hidden
                />
              </span>
            </span>
            <span className="mt-2 block truncate text-xs font-medium text-neutral-800">{label}</span>
          </span>
        ) : null}
      </span>
      <div className="min-w-0 flex-1">
        <span className="truncate">{label}</span>
      </div>
      <span className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-neutral-500">{badge}</span>
      {detail ? <span className="truncate text-[10px] text-neutral-500">{detail}</span> : null}
    </div>
  );
}
